import { articleSchema } from "@workspace/public-seo";
import { GuideEditorialDetails } from "@/components/GuideEditorialDetails";
import { useParams, Link } from "wouter";
import { SeoHead } from "@/components/SeoHead";
import { Calendar, Tag, ChevronLeft } from "lucide-react";
import NotFound from "@/pages/not-found";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@clerk/react";

interface GuideArticle {
  id: number;
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  tags: string[];
  coverImage: string;
  coverImageAlt: string;
  readingMinutes: number;
  content: string;
  authorName?: string | null;
  authorType?: string;
  reviewedAt?: string | null;
  sourceUrls?: string[];
  updatedAt?: string;
}

/** Format a YYYY-MM-DD date to Chinese display format, e.g. 2025年1月6日 */
function formatChineseDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return `${y}年${m}月${d}日`;
}

export default function GuideArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { isLoaded, isSignedIn, getToken } = useAuth();

  const { data: article, isLoading, isError } = useQuery<GuideArticle>({
    queryKey: ["guides", slug],
    queryFn: async () => {
      const res = await fetch(`/api/guides/${slug}`);
      if (res.status === 404) throw Object.assign(new Error("not-found"), { status: 404 });
      if (!res.ok) throw new Error("Failed to load article");
      return res.json();
    },
    enabled: !!slug,
    retry: false,
  });

  // Silent view tracking — fire-and-forget, never blocks or surfaces errors to user
  const trackedSlug = useRef<string | null>(null);
  useEffect(() => {
    if (!isLoaded || !article?.slug || trackedSlug.current === article.slug) return;
    trackedSlug.current = article.slug;
    const track = async () => {
      const token = isSignedIn ? await getToken() : null;
      // Never count a signed-in reader as anonymous if their auth token fails to load.
      if (isSignedIn && !token) return;
      await fetch(`/api/guides/${article.slug}/view`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    };
    track().catch(() => {});
  }, [article?.slug, getToken, isLoaded, isSignedIn]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b">
          <div className="bg-muted animate-pulse" style={{ height: "360px" }} />
          <div className="bg-secondary/30 py-8">
            <div className="container mx-auto px-4 md:px-8 max-w-3xl space-y-3">
              <div className="h-4 bg-muted rounded w-1/4 animate-pulse" />
              <div className="h-8 bg-muted rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !article) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={article.title}
        description={article.description}
        canonical={`/guides/${article.slug}`}
        structuredData={articleSchema(article)}
        ogImage={article.coverImage ? `https://www.skoltend.com${article.coverImage}` : undefined}
      />

      {/* Hero image */}
      <div className="border-b">
        {article.coverImage && (
          <div className="relative w-full overflow-hidden" style={{ maxHeight: "360px" }}>
            <img
              src={article.coverImage}
              alt={article.coverImageAlt}
              className="w-full object-cover object-center"
              style={{ maxHeight: "360px" }}
              width={1200}
              height={630}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
          </div>
        )}

        {/* Article header */}
        <div className="bg-secondary/30 py-8">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl space-y-3">
            {/* Back link */}
            <Link href="/guides">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                <ChevronLeft className="w-3 h-3" />
                採購知識中心
              </span>
            </Link>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5">
              {(article.tags ?? []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                >
                  <Tag className="w-2.5 h-2.5" />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold leading-snug">{article.title}</h1>
            <GuideEditorialDetails article={article} />
            <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">
              {article.description}
            </p>

            {/* Meta row */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatChineseDate(article.publishedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Article content — rendered from Markdown */}
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl">
        <div className="prose prose-sm max-w-none
          prose-headings:font-bold prose-headings:text-foreground
          prose-h2:text-lg prose-h2:border-b prose-h2:pb-2 prose-h2:mt-10 prose-h2:mb-4
          prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
          prose-p:text-foreground prose-p:leading-relaxed
          prose-strong:text-foreground
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline
          prose-ul:text-foreground prose-ol:text-foreground
          prose-li:text-foreground prose-li:leading-relaxed
          prose-table:text-sm prose-table:border prose-table:rounded-lg
          prose-thead:bg-muted/60
          prose-th:px-4 prose-th:py-3 prose-th:font-semibold prose-th:text-left
          prose-td:px-4 prose-td:py-3
          prose-tr:border-b
          prose-hr:border-border
        ">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Override table to add overflow wrapper
              table: ({ children }) => (
                <div className="overflow-x-auto rounded-lg border my-4">
                  <table className="w-full text-sm">{children}</table>
                </div>
              ),
              // Internal links: render as <a> (wouter handles navigation)
              a: ({ href, children }) => (
                <a href={href} className="text-primary hover:underline">
                  {children}
                </a>
              ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
